import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import React, { useCallback, useEffect, useMemo } from 'react'
import { Form, Formik, FormikProps, setNestedObjectValues } from 'formik'
import { ButtonV2, LoadingLogo, PlanType } from '@labrav/react-components'
import { useDispatch, useSelector } from '../../store'
import type { RootState } from '../../store/types'
import { Product } from '../../modules/productsListing/types'
import { FooterV2 } from '../../common/components/FooterV2/FooterV2'
import { isEmpty } from 'lodash'

import { useStyles } from './EditResaleAuthorizationWrapperV2.styles'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ResaleAuthorizationWithErrors } from '../../modules/resaleAuthorization/types'
import { ResellerInfo } from '../../types/resaleAuthorization'
import {
  clearResaleAuthorizationInCreation,
  getCurrentResaleAuthorizationInCreation,
  getDefaultResaleAuthorizationInCreation,
  setResaleAuthorizationInCreation,
  submitResaleAuthorization,
} from '../../modules/resaleAuthorization/action'
import { getProducts } from '../../modules/productsListing/action'
import { getResellersData } from '../../modules/reseller/action'
import { ScrollToFieldError, useAccountId } from '@labrav/common'
import { ResaleAuthorizationFormContainer } from './ResaleAuthorizationFormContainer/ResaleAuthorizationFormContainer'
import { Typography } from '@mui/material'
import clsx from 'clsx'
import { getMarketplaceReferralTemplateSalesforce } from '../../modules/marketplaceMappingManager/action'
import { CRMTypes } from '../../utils/constants'
import {
  LoadingTypes,
  isLoading,
  isLoading as Loading,
  useCurrentCloudProvider,
} from '@labrav/common'
import type { EditResaleAuthorizationWrapperV2Props } from './types'
import { getResaleAuthFormikValidationSchema } from '../../common/utils/resaleAuthFormikValidationSchema'

export type { EditResaleAuthorizationWrapperV2Props }
export type PrivateOfferContainerAddEditFormikProps =
  EditResaleAuthorizationWrapperV2Props

export const EditResaleAuthorizationWrapperV2: React.FC<
  EditResaleAuthorizationWrapperV2Props
> = ({
  externalBaseURL,
  search,
  crmOpportunityId,
  isSidePanelVisible = false,
  users,
}) => {
  const hasExternalBaseURL = !!externalBaseURL
  const selectedCloudTypeProductListing = useCurrentCloudProvider({
    microsoftResellerAuthorization: true,
  })
  const { classes } = useStyles()
  // const { productId: productIdParam, resaleAuthorizationId } = useParams<{
  //   productId: string
  //   resaleAuthorizationId: string
  // }>()
  const {
    cloud: routeCloud,
    accountId: routeAccountId,
    productId: productIdParam,
    resaleAuthorizationId,
    partnerId: partnerIdFromURL,
  } = useParams<{
    cloud: string
    accountId: string
    productId: string
    resaleAuthorizationId: string
    partnerId: string
  }>()
  const dispatch = useDispatch()
  const urlSearchParams = new URLSearchParams(search)
  const productId = productIdParam as string

  const isSalesforceRoute = useLocation().pathname.includes('/salesforce')
  const templatesData = useSelector(
    (state: RootState) => state.marketplaceMappingManager?.templateDetails || []
  )

  const crmObjectId = new URLSearchParams(search)
    .get('hs_object_id')
    ?.toLowerCase()

  const templateId = new URLSearchParams(search)
    .get('templateId')
    ?.toLowerCase()

  const crmId = useSelector<string | undefined>(
    state => state.AccountData.user.partnerData?.crmId || ''
  )
  const isDefaultOfferDataLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_REFERRAL_FORM_DATA)
  )

  // const partnerId = useSelector<string>(
  //   state => state.PartnerData.user.partnerData?.partnerId || ''
  // )
  const loadingSaveAsDraft = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.DRAFT_SAVE_RESALE_AUTHORIZATION)
  )
  const offerSubmitloading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.SAVE_RESALE_AUTHORIZATION)
  )
  const { products } = useFlyOutProductsBasedOnSelectedCloud({
    microsoftResellerAuthorization: true,
  })
  const resaleAuthorization = useSelector<ResaleAuthorizationWithErrors>(
    state =>
      state.resaleAuthorizationsInCreation.currentResaleAuthorizationInCreation[
        productId
      ] || ({} as ResaleAuthorizationWithErrors)
  )
  const product =
    products.find(product => product.productId === productId) || ({} as Product)

  const resellers = useSelector<ResellerInfo[]>(
    state => state.resellers.resellers
  )
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const cloudAccountId =
    new URLSearchParams(search || '').get('cloudAccountId') || ''
  const accountId: string =
    routeAccountId ||
    cloudAccountId ||
    (isSalesforceRoute ? partnerIdFromURL : '') ||
    fallbackAccountId ||
    ''

  const hasValidAccountId = routeAccountId
    ? !isEmpty(routeAccountId)
    : !isEmpty(fallbackAccountId)

  const fetchCurrentTemplateLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
  )
  const resellersLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.RESELLERS_DATA)
  )

  const baseURL = useMemo(() => {
    if (isSalesforceRoute)
      return `/external-apps/salesforce/partners/${accountId}/flyout/products-listing/${productId}`
    return (
      externalBaseURL ||
      `/flyout/${routeCloud}/${accountId}/products-listing/${productId}`
    )
  }, [externalBaseURL, productId, isSalesforceRoute, accountId, routeCloud])
  const navigate = useNavigate()

  useEffect(() => {
    const crmObjectIdToUse = crmObjectId || crmOpportunityId
    if (
      crmId &&
      crmObjectIdToUse &&
      templateId &&
      !resellersLoading &&
      !isEmpty(resellers)
    ) {
      const init = async () => {
        if (isSalesforceRoute) {
          await dispatch(
            getMarketplaceReferralTemplateSalesforce(
              crmId,
              accountId,
              templateId
            )
          )
        }
        if (accountId && isSalesforceRoute) {
          await dispatch(
            getDefaultResaleAuthorizationInCreation({
              productId,
              crmOpportunityId,
              crmId,
              crmObjectId: crmObjectIdToUse || '',
              templateId: templateId,
              accountId,
              crmType: CRMTypes.salesforce,
            })
          )
        }
      }

      init()
    }
  }, [
    crmId,
    crmObjectId,
    productId,
    crmOpportunityId,
    routeAccountId,
    isSalesforceRoute,
    templateId,
    resellersLoading,
    resellers,
  ])

  useEffect(() => {
    return () => {
      if (isSalesforceRoute) {
        dispatch(clearResaleAuthorizationInCreation(productId))
      }
    }
  }, [dispatch, isSalesforceRoute, productId])

  useEffect(() => {
    if (resaleAuthorization.hasBeenSent && !isEmpty(resaleAuthorization.id)) {
      const resaleAuthorizationId = resaleAuthorization.id

      // For internal URLs, baseURL already contains productId (defined as `/flyout/products-listing/${productId}`)
      const privateOfferDetailsPath = `${baseURL}/resale-authorizations/${resaleAuthorizationId}/details`
      navigate(
        {
          pathname: privateOfferDetailsPath,
          search,
        },
        { replace: true }
      )
      return () => {
        dispatch(clearResaleAuthorizationInCreation(productId))
      }
    } else if (
      !isEmpty(resaleAuthorization.id) &&
      resaleAuthorization.noOfTimesSaved === 1
    ) {
      // For internal URLs, baseURL already contains productId (defined as `/flyout/products-listing/${productId}`)
      const privateOfferEditPath = `${baseURL}/resale-authorizations/${resaleAuthorization.id}/edit`
      navigate(
        {
          pathname: privateOfferEditPath,
          search,
        },
        { replace: true }
      )
    }
  }, [
    resaleAuthorization.id,
    resaleAuthorization.hasBeenSent,
    resaleAuthorization.noOfTimesSaved,
  ])

  const productsLength = products.length
  const hasProducts = productsLength > 0

  useEffect(() => {
    if (
      hasValidAccountId &&
      !isEmpty(productId) &&
      (routeAccountId ? routeAccountId === accountId : true)
    ) {
      if (!hasProducts) {
        dispatch(getProducts(accountId, selectedCloudTypeProductListing))
      } else if (hasProducts && productId) {
        if (isEmpty(resellers) && !resellersLoading) {
          dispatch(getResellersData(accountId))
        }

        dispatch(
          getCurrentResaleAuthorizationInCreation({
            partnerId: accountId,
            productId,
            resaleAuthorizationId: resaleAuthorizationId || '',
            crmOpportunityId,
            cloud: selectedCloudTypeProductListing,
            labraNewUiRevamp: true,
          })
        )
      }
    }
  }, [
    routeAccountId,
    accountId,
    hasValidAccountId,
    hasProducts,
    productId,
    resaleAuthorizationId,
    crmOpportunityId,
    selectedCloudTypeProductListing,
  ])

  const canShowResaleAuthorization =
    !isEmpty(accountId) &&
    !isEmpty(resaleAuthorization) &&
    !isEmpty(product) &&
    resaleAuthorization.draft &&
    !isEmpty(resellers) &&
    !resellersLoading &&
    !fetchCurrentTemplateLoading &&
    !isDefaultOfferDataLoading

  const productDimensions = useMemo(() => {
    return (
      product?.productDimensions?.filter(
        dimension => dimension.isAdditionalUsageAllowed === false
      ) || []
    )
  }, [product])
  const dimensionsOptions = useMemo(() => {
    return productDimensions.map(dimension => dimension.label)
  }, [productDimensions])
  const resaleAuthFormikValidationSchema = useMemo(
    () =>
      getResaleAuthFormikValidationSchema(
        product.productType as PlanType,
        dimensionsOptions,
        hasExternalBaseURL
      ),
    [product.productType, dimensionsOptions, hasExternalBaseURL]
  )
  const submitCallBack = ({
    id,
    isDraft,
    resaleAuthorization: resaleAuthorizationData,
  }: {
    id?: string
    isDraft?: boolean
    resaleAuthorization?: Record<string, unknown>
  }) => {
    if (resaleAuthorizationData) {
      dispatch(
        setResaleAuthorizationInCreation({
          data: resaleAuthorizationData,
          errors: {},
          noOfTimesSaved: 2,
          productId,
        })
      )
    } else if (id && isDraft) {
      const resaleAuthorizationEditPath = `${baseURL}/resale-authorizations/${id}/edit`
      navigate(
        {
          pathname: resaleAuthorizationEditPath,
          search,
        },
        { replace: true }
      )
    } else if (!isDraft) {
      // For internal URLs, use baseURL (which already contains productId) or construct path with routeCloud/accountId
      const privateOfferDetailsPath = externalBaseURL
        ? `${baseURL}/resale-authorizations/${id}/details`
        : routeCloud && accountId
          ? `/flyout/${routeCloud}/${accountId}/products-listing/${productId}/resale-authorizations/${id}/details`
          : `${baseURL}/resale-authorizations/${id}/details`
      navigate(
        {
          pathname: privateOfferDetailsPath,
          search,
        },
        { replace: true }
      )
    }
  }
  const isOfferSaveAsDraftDisabled = useCallback(
    (formikProps: FormikProps<ResaleAuthorizationWithErrors>) => {
      const { errors, touched } = formikProps
      const dimensionsErr = touched?.dimensions && errors?.dimensions
      return dimensionsErr
    },
    []
  )

  const footerWidth =
    externalBaseURL && !isSidePanelVisible ? '100%' : 'calc(100% - 64px)'

  if (!canShowResaleAuthorization) {
    return <LoadingLogo />
  }

  return (
    <>
      {canShowResaleAuthorization && (
        <Formik
          initialValues={resaleAuthorization}
          validationSchema={resaleAuthFormikValidationSchema}
          validateOnMount={true}
          onSubmit={valuesData => {
            if (accountId && productId) {
              const creatorEmail = urlSearchParams?.get('creator_email')
              const creatorName = urlSearchParams?.get('creator_name')
              const hasCreatorData =
                hasExternalBaseURL && creatorEmail && creatorName
              dispatch(
                submitResaleAuthorization({
                  partnerId: accountId,
                  productId,
                  resaleAuthorization: valuesData,
                  saveAsDraft: false,
                  crmObjectLink: '',
                  crmOpportunityId,
                  submitCallBack,
                  labraNewUiRevamp: true,
                  ...(hasCreatorData
                    ? {
                        creatorData: {
                          creatorEmail,
                          creatorName,
                        },
                      }
                    : {}),
                })
              )
            }
          }}
          enableReinitialize
        >
          {formikProps => {
            // const handleSaveClick = async () => {
            //   await formikProps.submitForm()
            //   // if (Object.keys(formikProps.errors).length > 0) {
            //   //   return
            //   // }
            //   // sendCombinedFormData(formikProps.values)
            // }
            const handleSaveAsDraft = useCallback(
              ({ saveAsDraft }: { saveAsDraft: boolean }) => {
                if (accountId) {
                  dispatch(
                    submitResaleAuthorization({
                      partnerId: accountId,
                      productId,
                      resaleAuthorization: formikProps?.values,
                      saveAsDraft,
                      crmObjectLink: '',
                      crmOpportunityId,
                      submitCallBack,
                      labraNewUiRevamp: true,
                    })
                  )
                }
              },
              [formikProps, accountId, productId, crmOpportunityId]
            )
            const handleSubmit = async () => {
              const errors = await formikProps.validateForm()
              if (Object.keys(errors).length > 0) {
                formikProps.setTouched(
                  setNestedObjectValues(formikProps.values, true)
                )
                return
              }
              await formikProps.submitForm()
            }
            return (
              <Form>
                <ScrollToFieldError />
                <div
                  className={clsx(
                    classes.root,
                    isSalesforceRoute ? classes.salesforceWrapper : ''
                  )}
                  data-testid="formik-Container"
                >
                  <div className={classes?.container}>
                    <div className={classes?.formWidth}>
                      <div className={classes.productWrapper}>
                        <div className={classes.textWrapper}>
                          <Typography className={classes.textHead}>
                            Product
                          </Typography>
                          <Typography>{product.productName}</Typography>
                        </div>
                      </div>
                      <ResaleAuthorizationFormContainer
                        offset={260}
                        tabTop={'0px'}
                        product={product}
                        externalBaseURL={externalBaseURL}
                        users={users}
                      />
                    </div>
                  </div>
                </div>
                <FooterV2
                  actions={
                    <div className={classes.footerWrapper}>
                      <ButtonV2
                        type="button"
                        styleType="outlined"
                        onClick={() => handleSaveAsDraft({ saveAsDraft: true })}
                        data-testid={'save-as-draft'}
                        loading={loadingSaveAsDraft}
                        disabled={
                          isOfferSaveAsDraftDisabled(formikProps)
                            ? true
                            : undefined
                        }
                      >
                        {'Save as draft'}
                      </ButtonV2>
                      <ButtonV2
                        type="button"
                        data-testid="save-button-reseller-auth"
                        styleType="primary"
                        onClick={handleSubmit}
                        loading={offerSubmitloading}
                        disabled={offerSubmitloading}
                      >
                        Send to reseller
                      </ButtonV2>
                    </div>
                  }
                  width={footerWidth}
                  customClass={clsx(
                    classes.customFooter,
                    hasExternalBaseURL ? classes.externalFooter : ''
                  )}
                />
              </Form>
            )
          }}
        </Formik>
      )}
    </>
  )
}
